import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import {
  FileInfo,
  FileSource,
} from "@/components/feature/KnowledgebaseFileListItem/partials"
import {
  createDocumentTagMenuEntries,
  type DocumentTagEntry,
} from "@/components/feature/KnowledgebaseFilters/DocumentTagCategoryMenu"
import { DeleteDocumentModal } from "@/components/feature/Modals/DeleteDocumentModal/DeleteDocumentModal"
import {
  buildDocumentTagSavePayload,
  createDraftSelectedTags,
  getPersistedSelectedTagIdsByCategory,
  getPreservedHiddenTagIds,
  groupSelectedTagsByCategory,
  hasPendingTagSelectionChanges,
  sanitizeDraftSelectedTagIdsByCategory,
} from "@/components/feature/Modals/TagDocumentDialog/documentTagDraft"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  useCreateKnowledgeBaseTag,
  useGetDocumentTags,
  useRemoveDocumentTag,
  useSetDocumentTags,
} from "@/hooks/useKnowledgeBaseCategories/useKnowledgeBaseCategories"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { KnowledgebaseFile } from "@/types/knowledgebase"
import type {
  KnowledgeBaseCategory,
  KnowledgeBaseTag,
} from "@/types/knowledgebaseCategories"
import { classNames } from "@/utils/classNames"
import { knowledgeBaseErrorKey } from "@/utils/knowledgeBaseErrors"
import { isLimitError } from "@/utils/limitErrors"
import { type Row } from "@tanstack/react-table"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { InlineCategoryTagCellEditor } from "./InlineCategoryTagCellEditor"
import type {
  CategoryColumnMeta,
  KnowledgebaseActiveCell,
  KnowledgebaseColumnMeta,
  SetKnowledgebaseActiveCell,
} from "./KnowledgebaseFileTable"
import {
  TABLE_BORDER,
  type KnowledgebaseTableFeatures,
} from "./KnowledgebaseFileTable"
// ─── Row ──────────────────────────────────────────────────────────────────────

interface KnowledgebaseFileTableRowProps {
  row: Row<KnowledgebaseTableFeatures, KnowledgebaseFile>
  isLast: boolean
  selectedFileIds: Set<string>
  onToggleSelect: (id: string) => void
  isSelectionMode: boolean
  availableCategories: KnowledgeBaseCategory[]
  activeColumns: KnowledgeBaseCategory[]
  activeCell: KnowledgebaseActiveCell | null
  onActiveCellChange: SetKnowledgebaseActiveCell
  showAllTags?: boolean
  onOpenTagDialog?: (file: KnowledgebaseFile) => void
}

export const KnowledgebaseFileTableRow: React.FC<
  KnowledgebaseFileTableRowProps
> = ({
  row,
  isLast,
  selectedFileIds,
  onToggleSelect,
  isSelectionMode,
  availableCategories,
  activeColumns = [],
  activeCell,
  onActiveCellChange,
  showAllTags = true,
  onOpenTagDialog,
}) => {
  const item = row.original
  const { currentLanguage, translate } = useTranslation()
  const navigate = useNavigate()
  const kbTaggingFlowEnabled = useFeatureFlag("KB_TAGGING_FLOW")
  const rowRef = useRef<HTMLDivElement>(null)
  const isClosingActiveCellRef = useRef(false)

  const [isHover, setIsHover] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [draftSelectedTagIdsByCategory, setDraftSelectedTagIdsByCategory] =
    useState<Record<string, string[]>>({})

  const isSelected = selectedFileIds.has(item.id)
  const isSharepoint = item.source === "SHAREPOINT"
  const showCheckbox = isSelectionMode || isHover
  const { mutateAsync: createTag, isPending: isCreatingTag } =
    useCreateKnowledgeBaseTag()
  const { mutateAsync: setDocumentTags } = useSetDocumentTags()
  const { mutateAsync: removeDocumentTag } = useRemoveDocumentTag()

  const { data: documentTags } = useGetDocumentTags(
    item.id,
    kbTaggingFlowEnabled,
  )

  const allTagEntries = useMemo(() => {
    if (!documentTags?.categories) return []

    return createDocumentTagMenuEntries(documentTags.categories).filter(
      (entry) =>
        entry.tag.source === "user" ||
        (entry.tag.source === "auto" && entry.tag.approvedAt !== null),
    )
  }, [documentTags])

  const persistedSelectedTagIdsByCategory = useMemo(
    () => getPersistedSelectedTagIdsByCategory(allTagEntries),
    [allTagEntries],
  )

  const isActiveCategoryCell =
    activeCell !== null &&
    activeCell.fileId === item.id &&
    activeColumns.some((category) => category.id === activeCell.columnId)

  useEffect(() => {
    if (isActiveCategoryCell) return
    setDraftSelectedTagIdsByCategory(persistedSelectedTagIdsByCategory)
  }, [isActiveCategoryCell, persistedSelectedTagIdsByCategory])

  useEffect(() => {
    setDraftSelectedTagIdsByCategory((current) =>
      sanitizeDraftSelectedTagIdsByCategory({
        currentSelections: current,
        categories: availableCategories,
        persistedSelectedTagIdsByCategory,
      }),
    )
  }, [availableCategories, persistedSelectedTagIdsByCategory])

  const draftSelectedTags = useMemo(
    () =>
      createDraftSelectedTags({
        categories: availableCategories,
        draftSelectedTagIdsByCategory,
        allTagEntries,
        currentLanguage,
      }),
    [
      allTagEntries,
      availableCategories,
      currentLanguage,
      draftSelectedTagIdsByCategory,
    ],
  )

  const selectedTagsByCategory = useMemo(
    () => groupSelectedTagsByCategory(draftSelectedTags),
    [draftSelectedTags],
  )

  const preservedHiddenTagIds = useMemo(
    () => getPreservedHiddenTagIds(allTagEntries, availableCategories),
    [allTagEntries, availableCategories],
  )

  const hasPendingTagChanges = useMemo(
    () =>
      hasPendingTagSelectionChanges({
        categories: availableCategories,
        draftSelectedTagIdsByCategory,
        persistedSelectedTagIdsByCategory,
      }),
    [
      availableCategories,
      draftSelectedTagIdsByCategory,
      persistedSelectedTagIdsByCategory,
    ],
  )

  const handleItemClick = () => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set("file", item.id)
    navigate(`${window.location.pathname}?${searchParams.toString()}`, {
      replace: true,
    })
  }

  const commitDraftSelection = useCallback(async () => {
    if (!hasPendingTagChanges) return true

    const { nextTagIds, removedApprovedAutoTagIds } =
      buildDocumentTagSavePayload({
        documentTags,
        draftSelectedTagIdsByCategory,
        preservedHiddenTagIds,
      })

    try {
      const operations: Promise<unknown>[] = [
        setDocumentTags({ fileId: item.id, data: { tagIds: nextTagIds } }),
      ]

      for (const tagId of removedApprovedAutoTagIds) {
        operations.push(removeDocumentTag({ fileId: item.id, tagId }))
      }

      await Promise.all(operations)
      toast.success(translate("KNOWLEDGEBASE/FILE_TAG_DIALOG_UPDATE_SUCCESS"))
      return true
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("KNOWLEDGEBASE/FILE_TAG_DIALOG_UPDATE_ERROR"))
      }
      return false
    }
  }, [
    documentTags,
    draftSelectedTagIdsByCategory,
    hasPendingTagChanges,
    item.id,
    preservedHiddenTagIds,
    removeDocumentTag,
    setDocumentTags,
    translate,
  ])

  const handleCategorySelectionChange = useCallback(
    (categoryId: string, values: string[]) => {
      setDraftSelectedTagIdsByCategory((current) => ({
        ...current,
        [categoryId]: values,
      }))
    },
    [],
  )

  const handleRemoveDraftTag = useCallback((entry: DocumentTagEntry) => {
    setDraftSelectedTagIdsByCategory((current) => {
      const categoryId = entry.category.categoryId
      const existing = current[categoryId] ?? []
      return {
        ...current,
        [categoryId]: existing.filter((id) => id !== entry.tag.tagId),
      }
    })
  }, [])

  const handleCreateTag = useCallback(
    async (
      category: KnowledgeBaseCategory,
      tagName: string,
    ): Promise<KnowledgeBaseTag | undefined> => {
      if (!tagName.trim()) {
        toast.error(translate("KNOWLEDGEBASE/NAME_INVALID_ERROR"))
        return
      }

      try {
        const createdTag = await createTag({
          categoryId: category.id,
          data: { displayNameEn: tagName.trim() },
        })
        toast.success(
          translate("KNOWLEDGEBASE/TAG_CREATE_SUCCESS", { tag: tagName }),
        )
        return createdTag
      } catch (error) {
        if (!isLimitError(error)) {
          toast.error(
            translate(
              knowledgeBaseErrorKey(error, "KNOWLEDGEBASE/TAG_CREATE_ERROR"),
              { name: tagName },
            ),
          )
        }
      }
    },
    [createTag, translate],
  )

  const handleStaticCellActivate = useCallback(
    (columnId: string) => {
      onActiveCellChange({ fileId: item.id, columnId })
      onOpenTagDialog?.(item)
    },
    [item, item.id, onActiveCellChange, onOpenTagDialog],
  )

  const closeActiveCategoryCell = useCallback(async () => {
    if (isClosingActiveCellRef.current) return

    isClosingActiveCellRef.current = true
    const closingColumnId = activeCell?.columnId
    try {
      const didCommit = await commitDraftSelection()
      if (!didCommit) return
      onActiveCellChange((current) => {
        if (
          current?.fileId === item.id &&
          current.columnId === closingColumnId
        ) {
          return null
        }

        return current
      })
    } finally {
      isClosingActiveCellRef.current = false
    }
  }, [activeCell?.columnId, commitDraftSelection, item.id, onActiveCellChange])

  useEffect(() => {
    if (!isActiveCategoryCell) return

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      const activePortalOwnerId =
        activeCell?.fileId === item.id
          ? `${item.id}:${activeCell.columnId}`
          : null
      if (
        !target ||
        rowRef.current?.contains(target) ||
        (target instanceof Element &&
          activePortalOwnerId !== null &&
          target.closest(
            `[data-inline-tag-editor-owner="${activePortalOwnerId}"]`,
          ))
      ) {
        return
      }
      void closeActiveCategoryCell()
    }

    document.addEventListener("pointerdown", handlePointerDown)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [activeCell, closeActiveCategoryCell, isActiveCategoryCell, item.id])

  return (
    <>
      <div
        ref={rowRef}
        role="row"
        aria-label={`${translate("KNOWLEDGEBASE/FILE_ARIA_LABEL_ITEM")} ${item.fileName}`}
        className={classNames(
          "flex items-stretch",
          "min-h-20",
          "hover:bg-yellow1/10",
          "transition-opacity duration-200",
          !isLast && `border-b ${TABLE_BORDER}`,
        )}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onFocus={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node))
            setIsHover(true)
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node))
            setIsHover(false)
        }}
      >
        {row.getAllCells().map((cell) => {
          const meta = cell.column.columnDef.meta as
            | KnowledgebaseColumnMeta
            | undefined
          const categoryMeta =
            meta?.type === "category" ? (meta as CategoryColumnMeta) : null
          const isCellActive =
            activeCell?.fileId === item.id &&
            activeCell.columnId === cell.column.id
          const isCategoryCell = Boolean(categoryMeta)

          return (
            <div
              key={cell.id}
              role="cell"
              style={{ width: cell.column.getSize(), flexShrink: 0 }}
              tabIndex={isCategoryCell ? -1 : 0}
              className={classNames(
                `flex items-center border-r ${TABLE_BORDER}`,
                isCategoryCell && isCellActive
                  ? "relative z-20 overflow-visible"
                  : "overflow-hidden",
                isCategoryCell ? "px-0 py-0" : "px-3 py-3",
                !isCategoryCell ? "cursor-pointer" : "",
              )}
              onClick={(event) => {
                if (isCategoryCell) return
                const target = event.target as HTMLElement
                if (
                  target.closest(
                    "button, a, input, label, [role='menuitem'], [role='checkbox'], [data-stop-row-click]",
                  )
                ) {
                  return
                }
                handleStaticCellActivate(cell.column.id)
              }}
              onKeyDown={(event) => {
                if (isCategoryCell) return
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  handleStaticCellActivate(cell.column.id)
                }
              }}
              onBlur={async (event) => {
                if (!isCategoryCell || !isCellActive) return
                if (
                  event.currentTarget.contains(
                    event.relatedTarget as Node | null,
                  )
                ) {
                  return
                }
                await closeActiveCategoryCell()
              }}
            >
              {cell.column.id === "name" && (
                <FileInfo
                  item={item}
                  isHover={isHover}
                  onTitleClick={handleItemClick}
                  showCheckbox={showCheckbox}
                  isSelected={isSelected}
                  onToggleSelect={() => onToggleSelect(item.id)}
                  isCheckboxDisabled={isSharepoint}
                  showAllTags={showAllTags}
                  excludeCategoryIds={activeColumns.map((c) => c.id)}
                  handleTag={() => onOpenTagDialog?.(item)}
                  documentTags={kbTaggingFlowEnabled ? documentTags : undefined}
                  kbTaggingFlowEnabled={kbTaggingFlowEnabled}
                />
              )}

              {cell.column.id === "source" && (
                <FileSource source={item.source} />
              )}

              {cell.column.id === "date" && (
                <Typography as="p" variant="textS">
                  {item.createdAt.toLocaleDateString("de")}
                </Typography>
              )}

              {cell.column.id === "expiryDate" && (
                <Typography
                  as="p"
                  variant="textS"
                  className="text-muted-foreground"
                >
                  {item.expiryDate
                    ? new Date(item.expiryDate).toLocaleDateString("de")
                    : "—"}
                </Typography>
              )}

              {categoryMeta && (
                <InlineCategoryTagCellEditor
                  category={categoryMeta.category}
                  isCreatingTag={isCreatingTag}
                  portalOwnerId={`${item.id}:${categoryMeta.category.id}`}
                  selectedEntries={
                    selectedTagsByCategory[categoryMeta.category.id] ?? []
                  }
                  selectedTagIds={
                    draftSelectedTagIdsByCategory[categoryMeta.category.id] ??
                    []
                  }
                  isActive={isCellActive}
                  onActivate={() =>
                    onActiveCellChange({
                      fileId: item.id,
                      columnId: categoryMeta.category.id,
                    })
                  }
                  onSelectionChange={(values) =>
                    handleCategorySelectionChange(
                      categoryMeta.category.id,
                      values,
                    )
                  }
                  onRemoveTag={handleRemoveDraftTag}
                  onCreateTag={handleCreateTag}
                  onClose={closeActiveCategoryCell}
                />
              )}
            </div>
          )
        })}
      </div>

      {isDeleteModalOpen && (
        <DeleteDocumentModal
          fileId={item.id}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
        />
      )}
    </>
  )
}
